import React, { useCallback, useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import ClientShipmentInstructionsView from './views/ClientShipmentInstructionsView';
import {
    SHIPMENT_EMPTY_VALUE,
    SHIPMENT_FALLBACK_CLIENT_DATA,
    SHIPMENT_INVOICE_RECIPIENT,
    SHIPMENT_MOCK_CLIENT_DATA,
    SHIPMENT_STORAGE_KEYS,
    USE_MOCK_SHIPMENT_DATA
} from './service';
import { ClientQuoteDetailsService } from '../clientQuoteDetails/service/clientQuoteDetailsService';

const normalizeClientData = (source = {}, fallback = {}) => ({
    cnpj: source.cnpj || fallback.cnpj || SHIPMENT_EMPTY_VALUE,
    nome: source.nome || fallback.nome || SHIPMENT_EMPTY_VALUE,
    empresa: source.empresa || fallback.empresa || SHIPMENT_EMPTY_VALUE,
    telefone: source.telefone || fallback.telefone || SHIPMENT_EMPTY_VALUE,
    email: source.email || fallback.email || SHIPMENT_EMPTY_VALUE,
});

const readStoredClientData = () => {
    try {
        const stored = localStorage.getItem(SHIPMENT_STORAGE_KEYS.clientData);
        if (!stored) return null;
        const parsed = JSON.parse(stored);
        return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (_storageError) {
        return null;
    }
};

function ClientShipmentInstructionsPage({ currentLanguage, history, match }) {
    const [shipmentData, setShipmentData] = useState({
        estimateNumber: SHIPMENT_EMPTY_VALUE,
        clientData: normalizeClientData({}, SHIPMENT_FALLBACK_CLIENT_DATA),
        invoiceRecipient: SHIPMENT_INVOICE_RECIPIENT
    });
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const quoteId = match?.params?.id;

    const loadShipmentData = useCallback(async () => {
        try {
            setIsLoading(true);
            setError(null);

            if (quoteId) {
                // Buscar dados via API quando tiver ID
                const quoteData = await ClientQuoteDetailsService.getQuoteDetails(quoteId);
                setShipmentData({
                    estimateNumber: quoteData?.estimativa?.id || SHIPMENT_EMPTY_VALUE,
                    clientData: {
                        cnpj: quoteData?.cliente?.cnpj || SHIPMENT_EMPTY_VALUE,
                        nome: quoteData?.cliente?.nome || SHIPMENT_EMPTY_VALUE,
                        empresa: quoteData?.cliente?.empresa || SHIPMENT_EMPTY_VALUE,
                        telefone: quoteData?.cliente?.telefone || SHIPMENT_EMPTY_VALUE,
                        email: quoteData?.cliente?.email || SHIPMENT_EMPTY_VALUE,
                    },
                    invoiceRecipient: SHIPMENT_INVOICE_RECIPIENT
                });
            } else {
                // Manter comportamento atual (localStorage/fallback)
                const estimateNumber =
                    localStorage.getItem(SHIPMENT_STORAGE_KEYS.estimateNumber) || SHIPMENT_EMPTY_VALUE;

                const storedClientData = readStoredClientData();
                const baseClientData = USE_MOCK_SHIPMENT_DATA
                    ? SHIPMENT_MOCK_CLIENT_DATA
                    : storedClientData;

                const clientData = normalizeClientData(baseClientData, SHIPMENT_FALLBACK_CLIENT_DATA);

                setShipmentData({
                    estimateNumber,
                    clientData,
                    invoiceRecipient: SHIPMENT_INVOICE_RECIPIENT
                });
            }
        } catch (_requestError) {
            setError(currentLanguage.bepiService.client.client_shipment_load_error);
        } finally {
            setIsLoading(false);
        }
    }, [currentLanguage, quoteId]);

    useEffect(() => {
        loadShipmentData();
    }, [loadShipmentData]);

    const handlePrint = useCallback(() => {
        window.print();
    }, []);

    const handleBackToHome = useCallback(() => {
        history.push('/bepi/client');
    }, [history]);

    return (
        <ClientShipmentInstructionsView
            currentLanguage={currentLanguage}
            isLoading={isLoading}
            error={error}
            estimateNumber={shipmentData.estimateNumber}
            clientData={shipmentData.clientData}
            invoiceRecipient={shipmentData.invoiceRecipient}
            onPrint={handlePrint}
            onBackToHome={handleBackToHome}
        />
    );
}

export default withRouter(ClientShipmentInstructionsPage);
